export type Role = "member" | "lead" | "manager" | "admin";
export type Duty = "opener" | "closer" | "other";
export type Intent = "ready" | "break" | "lunch" | "training" | "meeting";
export type GhlState = "idle" | "busy" | "ended" | "unknown";
export type Status =
  | "available"
  | "busy"
  | "wrap_up"
  | "reserved"
  | "break"
  | "lunch"
  | "training"
  | "meeting"
  | "offline"
  | "unknown";
export interface Team {
  id: string;
  name: string;
  color: string;
  pilotEnabled: boolean;
}
export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  duty: Duty;
  bdrMember?: boolean;
  teamIds: string[];
  locationIds: string[];
  active: boolean;
  ghlUserId?: string | null;
}
export interface PilotAccess {
  enabled: boolean;
  reason: "enabled" | "team_disabled" | "unassigned";
}
export interface AuthConfig {
  ghlEnabled: boolean;
  ghlAppId: string | null;
  passwordEnabled: boolean;
  environment: "demo" | "local" | "server";
  hostedBridgeVersion?: 1;
  hostedToolbarVersion?: 1;
  mutationRequestVersion?: 1;
  mutationRevisionVersion?: 1;
  presenceRestrictionVersion?: 1;
  presenceFocusVersion?: 1;
}
export interface DirectoryLocation {
  id: string;
  name: string;
  companyId: string;
}
export interface DirectoryStaff {
  id: string;
  name: string;
  email: string;
  companyId: string;
  locationIds: string[];
  present: boolean;
  linkedUserId: string | null;
}
export interface AdminDirectorySnapshot {
  configured: boolean;
  demo: boolean;
  lastSyncAt: number | null;
  locations: DirectoryLocation[];
  users: DirectoryStaff[];
  warnings: string[];
}
export interface DirectoryProvisionResult {
  created: number;
  linked: number;
}
export interface SetupSnapshot {
  checks: {
    id: string;
    label: string;
    status: "ready" | "needs_setup" | "unverified";
    detail: string;
  }[];
  counts: {
    users: number;
    linkedUsers: number;
    unassignedUsers: number;
    teams: number;
    pilotTeams: number;
    stagedStaff: number;
  };
  lastDirectorySyncAt: number | null;
  serverTime: number;
  environment: "demo" | "local" | "server";
}
export interface DashboardHandoff {
  code: string;
  expiresAt: number;
}
export const MAX_GHL_CONTEXT_TABS = 20;
export interface PresenceEvidence {
  health: "fresh" | "stale" | "missing" | "restricted";
  reason:
    | "observed"
    | "call_continued"
    | "expired"
    | "no_observation"
    | "unreadable"
    | "unapproved_location"
    | "pilot_disabled"
    | "not_monitored"
    | "inactive";
  observedState: GhlState | null;
  validUntil: number | null;
  tabCount: number;
}
export interface AgentView {
  id: string;
  name: string;
  role: Role;
  duty: Duty;
  bdrMember?: boolean;
  activityMonitoringEnabled?: boolean;
  teamIds: string[];
  status: Status;
  intent: Intent;
  stateSince: number;
  lastSeen: number | null;
  source: "extension" | "demo" | "none";
  evidence?: PresenceEvidence;
}
export type TransferPeer = Pick<
  AgentView,
  "id" | "name" | "status" | "stateSince" | "lastSeen" | "evidence"
> & { duty?: Duty; bdrMember?: boolean };
export type TransferState =
  "pending" | "accepted" | "declined" | "cancelled" | "expired" | "completed";
export interface DashboardHandoffMetrics {
  windowStart: number;
  windowEnd: number;
  total: number;
  counts: Record<TransferState, number>;
}
export interface Transfer {
  id: string;
  fromUserId: string;
  toUserId: string;
  summary: string;
  state: TransferState;
  createdAt: number;
  expiresAt: number;
  updatedAt: number;
}
export type Disposition =
  | "sale"
  | "callback"
  | "not_interested"
  | "wrong_number"
  | "no_answer"
  | "other";
export interface QueueItem {
  id: string;
  ownerId: string;
  title: string;
  contactName: string;
  contactUrl: string;
  phone: string;
  notes: string;
  dueAt: number | null;
  status: "open" | "done" | "skipped";
  disposition: Disposition | null;
  createdAt: number;
  updatedAt: number;
}
export interface DeskScript {
  id: string;
  title: string;
  content: string;
  teamIds: string[];
  updatedAt: number;
}
export interface Activity {
  id: string;
  userId: string;
  type: string;
  message: string;
  createdAt: number;
}
export interface Snapshot {
  viewer: User;
  teams: Team[];
  agents: AgentView[];
  transfers: Transfer[];
  queueItems: QueueItem[];
  scripts: DeskScript[];
  activity: Activity[];
  handoffMetrics?: DashboardHandoffMetrics;
  serverTime: number;
  environment: "demo" | "local" | "server";
}
export interface HostedProductConfig {
  enabledModules: string[];
}
export function enabledProductModules(
  product: HostedProductConfig | undefined,
): string[] {
  return product?.enabledModules ?? ["availability"];
}
export interface AgentSnapshot {
  product?: HostedProductConfig;
  activityMonitoringEnabled?: boolean;
  availabilityDirectory?: TransferPeer[];
  viewer: User;
  self: AgentView;
  transferTargets: { id: string; name: string }[];
  transferDirectory?: TransferPeer[];
  transfers: (Transfer & { fromName: string; toName: string })[];
  queueItems: QueueItem[];
  scripts: DeskScript[];
  dashboardAllowed: boolean;
  canInitiateTransfers?: boolean;
  pilot: PilotAccess;
  serverTime: number;
  environment: "demo" | "local" | "server";
}
export const HOSTED_BRIDGE_VERSION = 1;
export interface HostedAlert {
  key: string;
  group: string;
  title: string;
  message: string;
  target: string;
  expiresAt: number;
}
export interface SessionRecoveryState {
  availableAfter: number;
  otherSessionCount: number;
}
export interface SessionRecoveryResult {
  signedOutLogins: number;
}
export interface AgentBridgeSnapshot {
  product?: HostedProductConfig;
  activityMonitoringEnabled?: boolean;
  protocolVersion: 1;
  viewer: Pick<User, "id" | "name" | "duty" | "bdrMember">;
  self: Pick<AgentView, "status" | "intent" | "lastSeen" | "source"> &
    Partial<Pick<AgentView, "stateSince" | "evidence">>;
  dashboardAllowed: boolean;
  canOpenDashboard?: boolean;
  canInitiateTransfers?: boolean;
  sessionRecovery?: SessionRecoveryState | null;
  eligibleCloserCount: number;
  eligiblePeerCount?: number;
  readyUntil?: number | null;
  toolbarVersion?: 1;
  inboxCount: number;
  pendingInbox: { id: string; expiresAt: number }[];
  dueCallbacks: { id: string; dueAt: number }[];
  dueCallbackCount: number;
  pilot: PilotAccess;
  alerts: HostedAlert[];
  serverTime: number;
  environment: "demo" | "local" | "server";
}
export function canAccessDashboard(role: Role): boolean {
  return role === "lead" || role === "manager" || role === "admin";
}
export type ObservationHealthCode =
  | "healthy"
  | "not_monitored"
  | "call_retained"
  | "waiting"
  | "no_ghl_tab"
  | "unreadable"
  | "stale"
  | "multiple_locations"
  | "too_many_tabs"
  | "unapproved_location"
  | "sign_in_required"
  | "server_unreachable"
  | "rejected";
export interface ObservationHealth {
  code: ObservationHealthCode;
  message: string;
  lastObservedAt: number | null;
  lastPublishedAt: number | null;
  tabCount: number;
}
export interface ExtensionSummary {
  connected: boolean;
  activityMonitoringEnabled?: boolean;
  environment?: "demo" | "local" | "server";
  error?: string;
  self:
    | (Pick<AgentView, "status" | "intent" | "lastSeen" | "source"> &
        Partial<Pick<AgentView, "stateSince" | "evidence">>)
    | null;
  viewerName?: string;
  viewerId?: string;
  duty?: Duty | null;
  dashboardAllowed: boolean;
  canInitiateTransfers?: boolean;
  sessionRecovery?: SessionRecoveryState | null;
  eligibleCloserCount: number;
  eligiblePeerCount?: number;
  readyUntil?: number | null;
  serverTime?: number;
  hostedToolbarSupported?: boolean;
  inboxCount: number;
  pendingInbox: { id: string; expiresAt: number }[];
  observation: ObservationHealth;
  pilot?: PilotAccess;
  dueCallbacks?: { id: string; dueAt: number }[];
  dueCallbackCount?: number;
  alerts?: HostedAlert[];
}
export interface AlertSettings {
  desktopNotifications: boolean;
  alertSounds: boolean;
}
export interface AuthResult {
  token: string;
  user: User;
}
export interface DemoPersona {
  id: string;
  name: string;
  role: Role;
  team: string;
}
export interface PresenceInput {
  deviceId: string;
  sequence: number;
  locationId: string | null;
  ghlState: GhlState;
  observedAt: number;
  tabs: number;
  ghlContext?: string;
  ghlContexts?: string[];
  ghlProofMode?: "selected";
  callContinuation?: true;
  observationUnavailable?: true;
  deviceClosed?: true;
}
export interface ExtensionConfig {
  serverUrl: string;
  token: string | null;
}
export type BridgeRequest =
  | {
      type: "desk:browser-observation";
      connectionId: string;
      command: import("./browser-observation").BrowserObservationCommand;
    }
  | {
      type: "desk:browser-observation-execute";
      command: import("./browser-observation").BrowserObservationCommand;
      owner: string;
    }
  | { type: "desk:config"; config?: ExtensionConfig }
  | {
      type: "desk:request";
      method: "GET" | "POST" | "PUT" | "PATCH";
      path: string;
      body?: unknown;
    }
  | {
      type: "desk:observation";
      locationId: string | null;
      state: GhlState;
      observedAt: number;
    }
  | { type: "desk:summary" }
  | { type: "desk:alert-settings"; settings?: AlertSettings }
  | { type: "desk:ghl-login"; automatic?: boolean }
  | { type: "desk:setup" }
  | { type: "desk:close-tools" }
  | { type: "desk:open-tools"; section: string }
  | { type: "desk:web-connect"; challenge: string }
  | { type: "desk:agent-connect"; challenge: string }
  | { type: "desk:hosted-config" }
  | { type: "desk:snooze-callbacks"; callbackIds: string[] }
  | { type: "desk:intent"; intent: Intent }
  | {
      type: "desk:recover-sessions";
      requestId: string;
      confirmOtherCallsEnded: true;
    }
  | { type: "desk:open" };
export type AgentAuthFailure =
  | "agent_parent_unavailable"
  | "agent_identity_rejected"
  | "agent_location_pending"
  | "agent_location_unavailable";

export interface BridgeResponse {
  ok: boolean;
  data?: unknown;
  error?: string;
  status?: number;
  authFailure?: AgentAuthFailure;
}
export const STATUS_LABELS: Record<Status, string> = {
  available: "Available",
  busy: "Busy",
  wrap_up: "Wrap-up",
  reserved: "Reserved",
  break: "On break",
  lunch: "Lunch",
  training: "Training",
  meeting: "Meeting",
  offline: "Offline",
  unknown: "Status unknown",
};
export const INTENT_LABELS: Record<Intent, string> = {
  ready: "Ready for transfers",
  break: "Take a break",
  lunch: "Lunch",
  training: "Training",
  meeting: "Meeting",
};
export const DISPOSITION_LABELS: Record<Disposition, string> = {
  sale: "Sale",
  callback: "Callback",
  not_interested: "Not interested",
  wrong_number: "Wrong number",
  no_answer: "No answer",
  other: "Other",
};
export const PRESENCE_TTL_MS = 35_000;
export const REQUEST_TTL_MS = 30_000;
export const ACCEPTED_TTL_MS = 120_000;
